import type { ElementType, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar } from '@/organisms/AppBar';
import { LeftNavigation, type NavItem } from '@/organisms/LeftNavigation';

/**
 * DashboardLayout
 * Classification: custom (template composing AppBar + LeftNavigation)
 *
 * The standard application shell: a top AppBar, a collapsible primary side
 * navigation, and a scrollable content region. Compose pages inside `children`.
 */
export interface DashboardLayoutProps {
  children: ReactNode;
  navItems: NavItem[];
  selectedNavId?: string;
  onNavSelect?: (id: string) => void;
  /** Brand text shown in the AppBar. */
  brand?: ReactNode;
  /** Right-aligned AppBar content (search, notifications, avatar). */
  toolbar?: ReactNode;
  collapsed?: boolean;
  onToggleNav?: () => void;
  /**
   * Link component used for `navItems` that carry an `href`. Pass a router link
   * (`next/link`, react-router's `Link`) for client-side navigation.
   */
  navLinkComponent?: ElementType;
}

export function DashboardLayout({
  children,
  navItems,
  selectedNavId,
  onNavSelect,
  brand = 'AiStrike',
  toolbar,
  collapsed = false,
  onToggleNav,
  navLinkComponent,
}: DashboardLayoutProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar sx={{ gap: 2 }}>
          <IconButton edge="start" aria-label="toggle navigation" onClick={onToggleNav}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontFamily: 'inherit' }}>
            {brand}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {toolbar}
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <LeftNavigation
          items={navItems}
          selectedId={selectedNavId}
          onSelect={onNavSelect}
          collapsed={collapsed}
          linkComponent={navLinkComponent}
        />
        <Box component="main" sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
